<template>
  <splide
    :options="updatedOptions"
    :has-track="false"
    class="product-slides"
    :class="$blockClass(blockName, 'product-slides')"
  >
    <splide-track>
      <splide-slide
        v-for="product of products"
        :key="product.id"
        @click="$emit('product-clicked', product)"
      >
        <product-card
          :class="$blockClass(blockName, 'card')"
          :block-class="blockName"
          :product="product"
          :image-options="{
            sizes: `(min-width: ${breakpoints.l}px) 30vw, 100vw`,
            minMax: '390-1024'
          }"
        />
      </splide-slide>
    </splide-track>

    <div class="splide__arrows splide__arrows--ltr">
      <button
        type="button"
        class="splide__arrow splide__arrow--prev"
        :aria-label="$string('accessibility.carousel.previous')"
        @click="$emit('navigation-clicked', false)"
      >
        <icon-arrow-left />
      </button>

      <button
        type="button"
        class="splide__arrow splide__arrow--next"
        :aria-label="$string('accessibility.carousel.next')"
        @click="$emit('navigation-clicked', false)"
      >
        <icon-arrow-right />
      </button>
    </div>
  </splide>
</template>

<script>

/**
 * Vue: product-slides (product-slides)
 * -----------------------------------------------------------------------------
 * Slides that feature product cards.
 * @param {Array} products - Array of products to display.
 * @param {Object} options - Options for the Splide carousel.
 * @param {String} blockName - Name of the block.
 *
 */

import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import { values as breakpoints } from '~/config/breakpoints'

import ProductCard from '~async/product-card/product-card'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'

export default {
  name: 'ProductSlides',
  components: {
    ProductCard,
    IconArrowLeft,
    IconArrowRight,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    products: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    blockName: {
      type: String,
      default: '',
      required: true,
    },
  },

  emits: {
    mounted: (instance) => Boolean(instance),
    'product-clicked': (product) => Boolean(product),
    'navigation-clicked': (direction) => typeof direction !== 'undefined',
  },

  data() {
    return {
      splideOptions: {
        mediaQuery: 'min',
        flickMaxPages: 1,
        focus: 'left',
        gap: 'var(--spacing-m)',
        perMove: 1,
        perPage: 1,
        wheel: false,
        arrows: true,
        drag: true,
        flickPower: 200,
        pagination: true,
        type: 'slide',
        omitEnd: true,
        autoplay: false,
        respectReducedMotion: true,
        breakpoints: {
          [breakpoints.m]: {
            gap: 'var(--spacing-xl)',
            perPage: 1,
          },
          [breakpoints.l]: {
            gap: 'var(--spacing-xl)',
            keyboard: true,
            perMove: 1,
            perPage: 3.1,
          },
        },
      },
      breakpoints,
    }
  },

  computed: {

    /**
     * Merge splideOptions with options passed in as props.
     * @return {Object}
     */
    updatedOptions() {
      return {
        ...this.splideOptions,
        ...this.options,
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-slides';
</style>
